import { Button } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { Textarea } from '@chiroup/components/form/Textarea';
import {
  ClickBehavior,
  DEFAULT_TRIAL_HOME,
  mergeTrialHomeWithDefaults,
  TRIAL_CARD_ICON_OPTIONS,
  TRIAL_TOUR_MANIFESTS,
  TRIAL_TOUR_OPTIONS,
  TrialCardIcon,
  TrialCardSection,
  TrialHomeCard,
  TrialHomeContent,
  TrialTourManifest,
  TrialTourStepManifest,
} from '@chiroup/core/types/TrialHome.type';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EyeIcon,
  PlusIcon,
  RocketLaunchIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, StarIcon } from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import appDataService, { TrialExperienceMeta } from '../../services/app-data.service';
import Uploader from '../common/uploader/Uploader';
import Header from '../layout/Header';
import TrialHomePreview from './TrialHomePreview';

dayjs.extend(relativeTime);

// Storage keys. Editor reads/writes the *Draft keys; the deploy endpoint
// promotes them to the live keys (trialHome / trialTourOverrides) that
// subscribers read — same save-vs-publish split as the home page builder.
const HOME_DRAFT_KEY = 'trialHomeDraft';
const HOME_LIVE_KEY = 'trialHome';
const OVERRIDES_DRAFT_KEY = 'trialTourOverridesDraft';
const OVERRIDES_LIVE_KEY = 'trialTourOverrides';
const META_KEY = 'trialExperienceMeta';

/**
 * Combined editor for the trial-user experience:
 *   - Home: page-level copy + card grid (the trial-home cards)
 *   - Tours: per-step copy overrides (title/body/proTip/nextLabel)
 *
 * Two-pane layout: left navigator selects one editable thing (page, card, or
 * tour-step), right pane is its form. Saves under two keys (`trialHome` for
 * card content, `trialTourOverrides` for tour copy); each side has independent
 * dirty tracking, so Save only writes what's actually changed.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StepOverride = {
  title?: string;
  body?: string;
  proTip?: string;
  nextLabel?: string;
  video?: string;
};

type OverridesByTour = Record<string, Record<string, StepOverride>>;

type Selection =
  | { kind: 'home-page' }
  | { kind: 'home-card'; cardId: string }
  | { kind: 'tour-step'; tourId: string; stepKey: string };

const SECTION_OPTIONS = [
  { text: 'Essentials', value: 'essentials' },
  { text: 'EHR', value: 'ehr' },
];

const CLICK_KIND_OPTIONS = [
  { text: 'Start an in-app tour', value: 'tour' },
  { text: 'Play a video (YouTube / Loom)', value: 'video' },
  { text: 'Navigate to a route', value: 'route' },
  { text: 'No action (informational)', value: 'none' },
];

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------

const TrialExperienceEditor = () => {
  // ---- Home content ----
  const [homeData, setHomeData] = useState<TrialHomeContent>(DEFAULT_TRIAL_HOME);
  const [savedHomeSnapshot, setSavedHomeSnapshot] = useState(JSON.stringify(DEFAULT_TRIAL_HOME));

  // ---- Tour overrides ----
  const [overrides, setOverrides] = useState<OverridesByTour>({});
  const [savedOverridesSnapshot, setSavedOverridesSnapshot] = useState('{}');

  // ---- UI state ----
  const [selection, setSelection] = useState<Selection>({ kind: 'home-page' });
  // Tours start collapsed so the sidebar isn't a wall of step rows on first open.
  const [expandedTours, setExpandedTours] = useState<Set<string>>(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Tracks publishedAt / lastEditedAt so the toolbar pill can distinguish
  // "Unsaved" (in-memory dirty), "Unpublished changes" (saved as draft but
  // not yet deployed), and "Up to date".
  const [meta, setMeta] = useState<TrialExperienceMeta | null>(null);

  // Load drafts + live + meta in parallel on mount. Editor prefers the
  // draft (last work-in-progress); if no draft exists yet, fall back to
  // live so admins keep editing where they left off.
  useEffect(() => {
    let destroyed = false;

    const load = async () => {
      const [homeDraftRes, homeLiveRes, overrideDraftRes, overrideLiveRes, metaRes] =
        await Promise.allSettled([
          appDataService.get(HOME_DRAFT_KEY),
          appDataService.get(HOME_LIVE_KEY),
          appDataService.get(OVERRIDES_DRAFT_KEY),
          appDataService.get(OVERRIDES_LIVE_KEY),
          appDataService.get(META_KEY),
        ]);

      if (destroyed) return;

      const homeDraft = homeDraftRes.status === 'fulfilled' ? homeDraftRes.value : null;
      const homeLive = homeLiveRes.status === 'fulfilled' ? homeLiveRes.value : null;
      const home = homeDraft || homeLive;
      if (home) {
        const merged = mergeTrialHomeWithDefaults(home);
        setHomeData(merged);
        setSavedHomeSnapshot(JSON.stringify(merged));
      }

      const overrideDraft = overrideDraftRes.status === 'fulfilled' ? overrideDraftRes.value : null;
      const overrideLive = overrideLiveRes.status === 'fulfilled' ? overrideLiveRes.value : null;
      const overridesSource = overrideDraft || overrideLive;
      if (overridesSource) {
        setOverrides(overridesSource as OverridesByTour);
        setSavedOverridesSnapshot(JSON.stringify(overridesSource));
      }

      if (metaRes.status === 'fulfilled' && metaRes.value) {
        setMeta(metaRes.value as TrialExperienceMeta);
      }
    };

    load();

    return () => {
      destroyed = true;
    };
  }, []);

  // ---- Dirty tracking ----
  const homeIsDirty = useMemo(
    () => JSON.stringify(homeData) !== savedHomeSnapshot,
    [homeData, savedHomeSnapshot]
  );
  const toursIsDirty = useMemo(
    () => JSON.stringify(overrides) !== savedOverridesSnapshot,
    [overrides, savedOverridesSnapshot]
  );
  const isDirty = homeIsDirty || toursIsDirty;

  // ---- Save (writes to DRAFT keys — subscribers don't see anything until
  // Deploy is clicked) ----
  const save = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const writes: Promise<unknown>[] = [];
      if (homeIsDirty) writes.push(appDataService.update(HOME_DRAFT_KEY, homeData));

      // Strip empty fields before persisting so the override blob stays
      // compact and `Object.keys(...)` checks remain meaningful at render.
      const cleaned = pruneEmpty(overrides);
      if (toursIsDirty) writes.push(appDataService.update(OVERRIDES_DRAFT_KEY, cleaned));

      await Promise.all(writes);

      if (homeIsDirty) setSavedHomeSnapshot(JSON.stringify(homeData));
      if (toursIsDirty) {
        setOverrides(cleaned);
        setSavedOverridesSnapshot(JSON.stringify(cleaned));
      }
      // Mirror the backend stamp client-side so the toolbar's
      // "Unpublished changes" indicator flips immediately without a refetch.
      setMeta(prev => ({ ...(prev ?? {}), lastEditedAt: new Date().toISOString() }));
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch {
      setSaveMessage('Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Deploy (promotes drafts to live in one transaction) ----
  const deploy = async () => {
    setIsDeploying(true);
    try {
      const res = await appDataService.deployTrialExperience();
      setMeta(prev => ({
        ...(prev ?? {}),
        publishedAt: res?.publishedAt ?? new Date().toISOString(),
        deployedBy: res?.deployedBy,
      }));
      setDeployModalOpen(false);
      setSaveMessage(res?.noChanges ? 'Nothing to deploy' : 'Deployed!');
      setTimeout(() => setSaveMessage(''), 2500);
    } catch {
      setSaveMessage('Error deploying');
    } finally {
      setIsDeploying(false);
    }
  };

  // Deploy status pill: "Unsaved" (in-memory dirty) > "Unpublished" (saved
  // but not deployed) > "Up to date".
  const deployStatus = useMemo(() => {
    const published = meta?.publishedAt ? dayjs(meta.publishedAt) : null;
    const edited = meta?.lastEditedAt ? dayjs(meta.lastEditedAt) : null;
    const hasUnpublished = !!edited && (!published || edited.isAfter(published));

    return {
      hasUnpublished,
      publishedAgo: published ? published.fromNow() : null,
      editedAgo: edited ? edited.fromNow() : null,
    };
  }, [meta]);

  // ---- Home mutators ----
  const updateHomeField = <K extends keyof TrialHomeContent>(
    field: K,
    value: TrialHomeContent[K]
  ) => setHomeData(prev => ({ ...prev, [field]: value }));

  const updateCard = (id: string, patch: Partial<TrialHomeCard>) =>
    setHomeData(prev => ({
      ...prev,
      cards: prev.cards.map(c => (c.id === id ? { ...c, ...patch } : c)),
    }));

  const reorderCard = (id: string, dir: 'up' | 'down') =>
    setHomeData(prev => {
      const card = prev.cards.find(c => c.id === id);
      if (!card) return prev;
      const sectionCards = prev.cards.filter(c => c.section === card.section);
      const idx = sectionCards.findIndex(c => c.id === id);
      const swapWith = sectionCards[dir === 'up' ? idx - 1 : idx + 1];
      if (!swapWith) return prev;

      const aIdx = prev.cards.findIndex(c => c.id === card.id);
      const bIdx = prev.cards.findIndex(c => c.id === swapWith.id);
      const next = [...prev.cards];
      [next[aIdx], next[bIdx]] = [next[bIdx], next[aIdx]];

      return { ...prev, cards: next };
    });

  const addCard = (section: TrialCardSection) => {
    const id = `custom-${Math.random().toString(36).slice(2, 8)}`;
    const newCard: TrialHomeCard = {
      id,
      enabled: true,
      section,
      icon: 'sparkles',
      title: 'New card',
      description: 'Describe this card.',
      clickBehavior: { kind: 'none' },
    };
    setHomeData(prev => ({ ...prev, cards: [...prev.cards, newCard] }));
    setSelection({ kind: 'home-card', cardId: id });
  };

  const removeCard = (id: string) => {
    setHomeData(prev => ({ ...prev, cards: prev.cards.filter(c => c.id !== id) }));
    // If the deleted card was selected, fall back to the page form.
    setSelection(prev =>
      prev.kind === 'home-card' && prev.cardId === id ? { kind: 'home-page' } : prev
    );
  };

  // ---- Tour mutators ----
  const setStepField = (
    tourId: string,
    stepKey: string,
    field: keyof StepOverride,
    value: string
  ) => {
    setOverrides(prev => {
      const tour = { ...(prev[tourId] || {}) };
      const step = { ...(tour[stepKey] || {}) };
      // Empty string means "no override" — store as missing so the code
      // default is used at render time.
      if (value.trim() === '') {
        delete step[field];
      } else {
        step[field] = value;
      }
      tour[stepKey] = step;

      return { ...prev, [tourId]: tour };
    });
  };

  const resetStep = (tourId: string, stepKey: string) => {
    setOverrides(prev => {
      const tour = { ...(prev[tourId] || {}) };
      delete tour[stepKey];

      return { ...prev, [tourId]: tour };
    });
  };

  const toggleTour = (tourId: string) =>
    setExpandedTours(prev => {
      const next = new Set(prev);
      if (next.has(tourId)) next.delete(tourId);
      else next.add(tourId);

      return next;
    });

  // Auto-expand a tour when one of its steps is selected, so the navigator
  // stays in sync with the editor pane.
  const selectStep = (tourId: string, stepKey: string) => {
    setExpandedTours(prev => {
      if (prev.has(tourId)) return prev;
      const next = new Set(prev);
      next.add(tourId);

      return next;
    });
    setSelection({ kind: 'tour-step', tourId, stepKey });
  };

  return (
    <>
      <Header
        title="Trial Experience"
        rightSide={
          <div className="flex items-center gap-3">
            <DeployStatusPill
              isDirty={isDirty}
              hasUnpublished={deployStatus.hasUnpublished}
              saveMessage={saveMessage}
            />
            <Link
              to="/home-page/trial-experience/analytics"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-sm font-medium text-gray-700 dark:text-dark-gray-200 transition-colors"
            >
              <ChartBarIcon className="h-4 w-4" />
              Analytics
            </Link>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-sm font-medium text-gray-700 dark:text-dark-gray-200 transition-colors"
            >
              <EyeIcon className="h-4 w-4" />
              Preview home
            </button>
            <Button
              text={isDirty ? 'Save draft' : 'Saved'}
              onClick={save}
              loading={isSaving}
              disabled={!isDirty || isDeploying}
            />
            <button
              type="button"
              onClick={() => setDeployModalOpen(true)}
              disabled={isDirty || !deployStatus.hasUnpublished || isDeploying || isSaving}
              title={
                isDirty
                  ? 'Save your draft before deploying.'
                  : !deployStatus.hasUnpublished
                    ? 'No changes to deploy.'
                    : 'Make this draft live for all trial users.'
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RocketLaunchIcon className="h-4 w-4" />
              Deploy
            </button>
          </div>
        }
      />
      <div className="flex gap-4 m-4 min-h-[calc(100vh-8rem)]">
        <Sidebar
          homeData={homeData}
          overrides={overrides}
          selection={selection}
          expandedTours={expandedTours}
          onSelect={setSelection}
          onToggleTour={toggleTour}
          onAddCard={addCard}
        />
        <main className="flex-1 min-w-0">
          <EditorPane
            selection={selection}
            homeData={homeData}
            overrides={overrides}
            onUpdateHomeField={updateHomeField}
            onUpdateCard={updateCard}
            onReorderCard={reorderCard}
            onRemoveCard={removeCard}
            onChangeStepField={setStepField}
            onResetStep={resetStep}
          />
        </main>
      </div>

      <PreviewModal
        open={previewOpen}
        data={homeData}
        selectedCardId={selection.kind === 'home-card' ? selection.cardId : null}
        onSelectCard={id => {
          setSelection({ kind: 'home-card', cardId: id });
          setPreviewOpen(false);
        }}
        onClose={() => setPreviewOpen(false)}
      />

      <ConfirmModal
        isOpen={deployModalOpen}
        title="Deploy trial experience?"
        confirmText={isDeploying ? 'Deploying...' : 'Deploy'}
        cancelText="Cancel"
        loading={isDeploying}
        confirmDisabled={isDeploying}
        confirm={deploy}
        close={() => !isDeploying && setDeployModalOpen(false)}
        description={
          <span>
            This will replace the live trial home + tour copy for every clinic on the trial plan
            with your current draft.
          </span>
        }
      >
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-left">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-gray-500">Last deployed</dt>
            <dd className="font-medium text-gray-900">{deployStatus.publishedAgo ?? 'Never'}</dd>
            <dt className="text-gray-500">Last edited</dt>
            <dd className="font-medium text-gray-900">{deployStatus.editedAgo ?? '—'}</dd>
          </dl>
        </div>
      </ConfirmModal>
    </>
  );
};

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

const Sidebar: React.FC<{
  homeData: TrialHomeContent;
  overrides: OverridesByTour;
  selection: Selection;
  expandedTours: Set<string>;
  onSelect: (s: Selection) => void;
  onToggleTour: (tourId: string) => void;
  onAddCard: (section: TrialCardSection) => void;
}> = ({ homeData, overrides, selection, expandedTours, onSelect, onToggleTour, onAddCard }) => {
  const isPageSelected = selection.kind === 'home-page';

  return (
    <aside className="w-72 shrink-0 bg-white dark:bg-dark-gray-800 border border-gray-200 dark:border-dark-gray-700 rounded-lg overflow-y-auto sticky top-4 self-start max-h-[calc(100vh-6rem)]">
      <SidebarGroup label="Home">
        <SidebarRow
          label="Page settings"
          hint="Heading · section labels · CTA"
          selected={isPageSelected}
          onClick={() => onSelect({ kind: 'home-page' })}
        />
      </SidebarGroup>

      <SidebarSubgroup label="Essentials feature cards" onAdd={() => onAddCard('essentials')}>
        {homeData.cards
          .filter(c => c.section === 'essentials')
          .map(card => (
            <SidebarRow
              key={card.id}
              label={card.title || 'Untitled card'}
              hint={card.enabled ? undefined : 'Hidden'}
              selected={selection.kind === 'home-card' && selection.cardId === card.id}
              dim={!card.enabled}
              onClick={() => onSelect({ kind: 'home-card', cardId: card.id })}
            />
          ))}
      </SidebarSubgroup>

      <SidebarSubgroup label="EHR feature cards" onAdd={() => onAddCard('ehr')}>
        {homeData.cards
          .filter(c => c.section === 'ehr')
          .map(card => (
            <SidebarRow
              key={card.id}
              label={card.title || 'Untitled card'}
              hint={card.enabled ? undefined : 'Hidden'}
              selected={selection.kind === 'home-card' && selection.cardId === card.id}
              dim={!card.enabled}
              onClick={() => onSelect({ kind: 'home-card', cardId: card.id })}
            />
          ))}
      </SidebarSubgroup>

      <SidebarGroup label="Tours" topBorder>
        {TRIAL_TOUR_MANIFESTS.map(tour => {
          const expanded = expandedTours.has(tour.id);
          const tourOverrides = overrides[tour.id] || {};
          const editedCount = Object.keys(tourOverrides).filter(stepKey =>
            hasAnyOverride(tourOverrides[stepKey])
          ).length;

          return (
            <div key={tour.id} className="px-1">
              <button
                type="button"
                onClick={() => onToggleTour(tour.id)}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-sm text-gray-700 dark:text-dark-gray-200 hover:bg-gray-50 dark:hover:bg-dark-gray-700 transition-colors"
              >
                {expanded ? (
                  <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                ) : (
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                )}
                <span className="flex-1 truncate">{tour.label}</span>
                {editedCount > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    {editedCount}
                  </span>
                )}
              </button>
              {expanded && (
                <div className="pl-5 mt-0.5 mb-1 flex flex-col gap-0.5">
                  {tour.steps.map((step, i) => {
                    const isSelected =
                      selection.kind === 'tour-step' &&
                      selection.tourId === tour.id &&
                      selection.stepKey === step.stepKey;
                    const edited = hasAnyOverride(tourOverrides[step.stepKey]);

                    return (
                      <SidebarRow
                        key={step.stepKey}
                        label={`${i + 1}. ${step.title || step.stepKey}`}
                        selected={isSelected}
                        edited={edited}
                        size="sm"
                        onClick={() =>
                          onSelect({ kind: 'tour-step', tourId: tour.id, stepKey: step.stepKey })
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </SidebarGroup>
    </aside>
  );
};

const SidebarGroup: React.FC<{
  label: string;
  children: React.ReactNode;
  topBorder?: boolean;
}> = ({ label, children, topBorder }) => (
  <div
    className={
      topBorder ? 'pt-3 pb-2 border-t border-gray-100 dark:border-dark-gray-700' : 'pt-3 pb-2'
    }
  >
    <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-gray-500">
      {label}
    </div>
    {children}
  </div>
);

const SidebarSubgroup: React.FC<{
  label: string;
  onAdd: () => void;
  children: React.ReactNode;
}> = ({ label, onAdd, children }) => (
  <div className="px-1 pb-2">
    <div className="flex items-center justify-between px-2 py-1">
      <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-dark-gray-500">
        {label}
      </div>
      <button
        type="button"
        onClick={onAdd}
        aria-label={`Add ${label} card`}
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-dark-gray-200 transition-colors"
      >
        <PlusIcon className="h-3.5 w-3.5" />
      </button>
    </div>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

const SidebarRow: React.FC<{
  label: string;
  hint?: string;
  selected: boolean;
  dim?: boolean;
  edited?: boolean;
  size?: 'sm' | 'md';
  onClick: () => void;
}> = ({ label, hint, selected, dim, edited, size = 'md', onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'w-full text-left px-2 rounded-md transition-colors',
      size === 'sm' ? 'py-1' : 'py-1.5',
      selected
        ? 'bg-primary-600 text-white hover:bg-primary-700'
        : 'text-gray-700 dark:text-dark-gray-200 hover:bg-gray-50 dark:hover:bg-dark-gray-700',
      dim ? 'opacity-60' : '',
    ].join(' ')}
  >
    <div className="flex items-center gap-2">
      <span className={['flex-1 truncate', size === 'sm' ? 'text-xs' : 'text-sm'].join(' ')}>
        {label}
      </span>
      {edited && (
        <span
          className={[
            'h-1.5 w-1.5 rounded-full shrink-0',
            selected ? 'bg-white' : 'bg-amber-500',
          ].join(' ')}
        />
      )}
    </div>
    {hint && (
      <div
        className={[
          'truncate text-[11px]',
          selected ? 'text-white/80' : 'text-gray-400 dark:text-dark-gray-500',
        ].join(' ')}
      >
        {hint}
      </div>
    )}
  </button>
);

// ---------------------------------------------------------------------------
// Editor pane — switches on selection
// ---------------------------------------------------------------------------

const EditorPane: React.FC<{
  selection: Selection;
  homeData: TrialHomeContent;
  overrides: OverridesByTour;
  onUpdateHomeField: <K extends keyof TrialHomeContent>(
    field: K,
    value: TrialHomeContent[K]
  ) => void;
  onUpdateCard: (id: string, patch: Partial<TrialHomeCard>) => void;
  onReorderCard: (id: string, dir: 'up' | 'down') => void;
  onRemoveCard: (id: string) => void;
  onChangeStepField: (
    tourId: string,
    stepKey: string,
    field: keyof StepOverride,
    value: string
  ) => void;
  onResetStep: (tourId: string, stepKey: string) => void;
}> = ({
  selection,
  homeData,
  overrides,
  onUpdateHomeField,
  onUpdateCard,
  onReorderCard,
  onRemoveCard,
  onChangeStepField,
  onResetStep,
}) => {
  if (selection.kind === 'home-page') {
    return <HomePageForm data={homeData} onChange={onUpdateHomeField} />;
  }

  if (selection.kind === 'home-card') {
    const card = homeData.cards.find(c => c.id === selection.cardId);
    if (!card) {
      return <EmptyState message="This card no longer exists. Pick something on the left." />;
    }
    const sectionCards = homeData.cards.filter(c => c.section === card.section);
    const idx = sectionCards.findIndex(c => c.id === card.id);

    return (
      <HomeCardForm
        card={card}
        canMoveUp={idx > 0}
        canMoveDown={idx < sectionCards.length - 1}
        onUpdate={patch => onUpdateCard(card.id, patch)}
        onMoveUp={() => onReorderCard(card.id, 'up')}
        onMoveDown={() => onReorderCard(card.id, 'down')}
        onDelete={() => {
          if (window.confirm(`Delete "${card.title || card.id}"?`)) {
            onRemoveCard(card.id);
          }
        }}
      />
    );
  }

  // selection.kind === 'tour-step'
  const tour = TRIAL_TOUR_MANIFESTS.find(t => t.id === selection.tourId);
  const step = tour?.steps.find(s => s.stepKey === selection.stepKey);
  if (!tour || !step) {
    return <EmptyState message="This step no longer exists. Pick something on the left." />;
  }
  const stepNumber = tour.steps.findIndex(s => s.stepKey === selection.stepKey) + 1;
  const override = overrides[tour.id]?.[step.stepKey] || {};

  return (
    <TourStepForm
      tour={tour}
      step={step}
      stepNumber={stepNumber}
      override={override}
      onChangeField={(field, value) => onChangeStepField(tour.id, step.stepKey, field, value)}
      onReset={() => onResetStep(tour.id, step.stepKey)}
    />
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center h-full">
    <p className="text-sm text-gray-500 dark:text-dark-gray-400 italic">{message}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Home page form (heading / section labels / CTA)
// ---------------------------------------------------------------------------

const HomePageForm: React.FC<{
  data: TrialHomeContent;
  onChange: <K extends keyof TrialHomeContent>(field: K, value: TrialHomeContent[K]) => void;
}> = ({ data, onChange }) => (
  <Panel title="Page settings" subtitle="Shown at the top of every trial user's home view.">
    <Input
      name="heading"
      label="Page heading"
      value={data.heading}
      onChange={val => onChange('heading', val)}
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
      <Input
        name="essentialsLabel"
        label="Essentials section label"
        value={data.essentialsLabel}
        onChange={val => onChange('essentialsLabel', val)}
      />
      <Input
        name="ehrLabel"
        label="EHR section label"
        value={data.ehrLabel}
        onChange={val => onChange('ehrLabel', val)}
      />
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Kickstart hero
    </h3>
    <Input
      name="kickstartHeading"
      label="Heading"
      value={data.kickstartHeading || ''}
      onChange={val => onChange('kickstartHeading', val)}
    />

    <div className="mt-4 rounded-lg border border-gray-200 dark:border-dark-gray-700 p-4">
      <Checkbox
        name="kickstartTestimonialEnabled"
        label="Show testimonial"
        value={data.kickstartTestimonialEnabled ?? true}
        onChange={val => onChange('kickstartTestimonialEnabled', val)}
      />
      <Textarea
        name="kickstartTestimonial"
        label="Review text"
        value={data.kickstartTestimonial || ''}
        onChange={val => onChange('kickstartTestimonial', val)}
        rows={3}
        className="mt-3"
      />
      <Input
        name="kickstartTestimonialAuthor"
        label="Reviewer name"
        value={data.kickstartTestimonialAuthor || ''}
        onChange={val => onChange('kickstartTestimonialAuthor', val)}
        className="mt-3"
      />
      <Uploader
        name="kickstartTestimonialAvatar"
        label="Reviewer avatar"
        className="mt-3"
        value={data.kickstartTestimonialAvatar || ''}
        onChange={val => onChange('kickstartTestimonialAvatar', val ?? '')}
        type="image"
        uploadCategory="loginContent"
        uploadType="image"
        fileTypes={['png', 'jpg', 'jpeg', 'gif', 'webp']}
        fileSizeLimit={10}
        square
      />
      <div className="mt-3">
        <span className="block text-sm font-medium text-gray-700 dark:text-dark-gray-300 mb-1">
          Star rating
        </span>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => onChange('kickstartTestimonialRating', n)}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
            >
              <StarIcon
                className={`h-6 w-6 ${
                  (data.kickstartTestimonialRating ?? 5) >= n
                    ? 'text-yellow-400'
                    : 'text-gray-300 dark:text-dark-gray-600'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Bottom CTA
    </h3>
    <Input
      name="ctaTitle"
      label="Title"
      value={data.ctaTitle}
      onChange={val => onChange('ctaTitle', val)}
    />
    <Input
      name="ctaButton"
      label="Button text"
      value={data.ctaButton}
      onChange={val => onChange('ctaButton', val)}
      className="mt-3"
    />
    <Textarea
      name="ctaSubtitle"
      label="Subtitle (small fine-print under the button)"
      value={data.ctaSubtitle || ''}
      onChange={val => onChange('ctaSubtitle', val)}
      rows={2}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrl"
      label="Schedule-a-call link — Trial / default (opens embedded; used by this button and the checklist CTA)"
      value={data.scheduleCallUrl || ''}
      onChange={val => onChange('scheduleCallUrl', val)}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrlBasic"
      label="Schedule-a-call link — Basic (blank = use the Trial / default link)"
      value={data.scheduleCallUrlBasic || ''}
      onChange={val => onChange('scheduleCallUrlBasic', val)}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrlPlus"
      label="Schedule-a-call link — Plus (blank = use the Trial / default link)"
      value={data.scheduleCallUrlPlus || ''}
      onChange={val => onChange('scheduleCallUrlPlus', val)}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrlPremium"
      label="Schedule-a-call link — Premium (blank = use the Trial / default link)"
      value={data.scheduleCallUrlPremium || ''}
      onChange={val => onChange('scheduleCallUrlPremium', val)}
      className="mt-3"
    />

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Checklist bonus step
    </h3>
    <div className="rounded-lg border border-gray-200 dark:border-dark-gray-700 p-4">
      <Checkbox
        name="bonusStepEnabled"
        label="Show bonus step in the checklist"
        value={data.bonusStepEnabled ?? true}
        onChange={val => onChange('bonusStepEnabled', val)}
      />
      <Input
        name="bonusStepTitle"
        label="Title"
        value={data.bonusStepTitle || ''}
        onChange={val => onChange('bonusStepTitle', val)}
        className="mt-3"
      />
      <Textarea
        name="bonusStepSubtitle"
        label="Subtitle"
        value={data.bonusStepSubtitle || ''}
        onChange={val => onChange('bonusStepSubtitle', val)}
        rows={2}
        className="mt-3"
      />
      <Input
        name="bonusStepBadge"
        label="Badge"
        value={data.bonusStepBadge || ''}
        onChange={val => onChange('bonusStepBadge', val)}
        className="mt-3"
      />
      <Input
        name="bonusStepButtonText"
        label="Button label"
        value={data.bonusStepButtonText || ''}
        onChange={val => onChange('bonusStepButtonText', val)}
        className="mt-3"
      />
      <Uploader
        name="bonusStepImage"
        label="Expert photo"
        className="mt-3"
        value={data.bonusStepImage || ''}
        onChange={val => onChange('bonusStepImage', val ?? '')}
        type="image"
        uploadCategory="loginContent"
        uploadType="image"
        fileTypes={['png', 'jpg', 'jpeg', 'gif', 'webp']}
        fileSizeLimit={10}
        publicFile
        previewClassName="h-24 w-24 rounded-full object-cover"
        customLabel={data.bonusStepImage ? 'Remove' : undefined}
        onClickCustom={() => onChange('bonusStepImage', '')}
      />
      <p className="mt-2 text-xs text-gray-500 dark:text-dark-gray-400">
        Opens the schedule-a-call link above. It's a bonus — it doesn't count toward checklist
        completion.
      </p>
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Checklist: Select my plan
    </h3>
    <div className="rounded-lg border border-gray-200 dark:border-dark-gray-700 p-4">
      <Checkbox
        name="selectPlanEnabled"
        label="Show the Select my plan CTA"
        value={data.selectPlanEnabled ?? true}
        onChange={val => onChange('selectPlanEnabled', val)}
      />
      <Input
        name="selectPlanTitle"
        label="Title"
        value={data.selectPlanTitle || ''}
        onChange={val => onChange('selectPlanTitle', val)}
        className="mt-3"
      />
      <Textarea
        name="selectPlanSubtitle"
        label="Subtitle"
        value={data.selectPlanSubtitle || ''}
        onChange={val => onChange('selectPlanSubtitle', val)}
        rows={2}
        className="mt-3"
      />
      <p className="mt-2 text-xs text-gray-500 dark:text-dark-gray-400">
        Starts Essentials checkout. Shown to clinic admins only; doesn't count toward checklist
        completion.
      </p>
    </div>
  </Panel>
);

// ---------------------------------------------------------------------------
// Home card form
// ---------------------------------------------------------------------------

const HomeCardForm: React.FC<{
  card: TrialHomeCard;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onUpdate: (patch: Partial<TrialHomeCard>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}> = ({ card, canMoveUp, canMoveDown, onUpdate, onMoveUp, onMoveDown, onDelete }) => (
  <Panel
    title={card.title || 'Untitled card'}
    subtitle={card.id}
    rightSide={
      <div className="flex items-center gap-1">
        <IconButton
          icon={<ArrowUpIcon className="h-3.5 w-3.5" />}
          label="Move up"
          onClick={onMoveUp}
          disabled={!canMoveUp}
        />
        <IconButton
          icon={<ArrowDownIcon className="h-3.5 w-3.5" />}
          label="Move down"
          onClick={onMoveDown}
          disabled={!canMoveDown}
        />
        <IconButton
          icon={<TrashIcon className="h-3.5 w-3.5" />}
          label="Delete card"
          onClick={onDelete}
          tone="danger"
        />
      </div>
    }
  >
    <div className="flex items-center justify-between mb-1">
      <Checkbox
        name={`enabled-${card.id}`}
        label="Visible to users"
        value={card.enabled}
        onChange={val => onUpdate({ enabled: val })}
      />
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Content
    </h3>
    <Input
      name={`title-${card.id}`}
      label="Title"
      value={card.title}
      onChange={val => onUpdate({ title: val })}
    />
    <Textarea
      name={`description-${card.id}`}
      label="Description"
      value={card.description}
      onChange={val => onUpdate({ description: val })}
      rows={2}
      className="mt-3"
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
      <Input
        name={`badge-${card.id}`}
        label='Badge (optional, e.g., "Start here")'
        value={card.badge || ''}
        onChange={val => onUpdate({ badge: val })}
      />
      <Select
        name={`icon-${card.id}`}
        label="Icon"
        value={card.icon}
        options={TRIAL_CARD_ICON_OPTIONS.map(o => ({ text: o.label, value: o.id }))}
        onChange={(v: string) => onUpdate({ icon: v as TrialCardIcon })}
        alwaysReturnArray={false}
      />
    </div>
    <Select
      name={`section-${card.id}`}
      label="Section"
      value={card.section}
      options={SECTION_OPTIONS}
      onChange={(v: string) => onUpdate({ section: v as TrialCardSection })}
      alwaysReturnArray={false}
      className="mt-3"
    />

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      On click
    </h3>
    <ClickBehaviorEditor
      cardId={card.id}
      value={card.clickBehavior}
      onChange={cb => onUpdate({ clickBehavior: cb })}
    />
  </Panel>
);

// ---------------------------------------------------------------------------
// Tour step form
// ---------------------------------------------------------------------------

const TourStepForm: React.FC<{
  tour: TrialTourManifest;
  step: TrialTourStepManifest;
  stepNumber: number;
  override: StepOverride;
  onChangeField: (field: keyof StepOverride, value: string) => void;
  onReset: () => void;
}> = ({ tour, step, stepNumber, override, onChangeField, onReset }) => {
  const edited = hasAnyOverride(override);

  return (
    <Panel
      title={`${tour.label} — Step ${stepNumber}`}
      subtitle={step.hint || step.stepKey}
      rightSide={
        edited ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-dark-gray-600 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-xs font-medium text-gray-600 dark:text-dark-gray-300 transition-colors shrink-0"
          >
            <ArrowUturnLeftIcon className="h-3 w-3" />
            Reset to default
          </button>
        ) : null
      }
    >
      <p className="text-xs text-gray-500 dark:text-dark-gray-400 mb-4">
        Anchors and step order are wired in code — marketing only edits the words. Leave a field
        blank to use the code default.
      </p>

      <OverridableField
        label="Title"
        stepKey={step.stepKey}
        field="title"
        defaultValue={step.title}
        override={override.title}
        onChange={value => onChangeField('title', value)}
      />
      <OverridableField
        label="Body"
        stepKey={step.stepKey}
        field="body"
        defaultValue={step.body}
        override={override.body}
        onChange={value => onChangeField('body', value)}
        multiline
        rows={5}
        help='Plain text. Use blank lines for paragraph breaks. Wrap a word in **stars** for bold (e.g., "**Plan** — evidence-based treatments").'
      />
      <OverridableField
        label="Pro tip (italic line under the body)"
        stepKey={step.stepKey}
        field="proTip"
        defaultValue={step.proTip}
        override={override.proTip}
        onChange={value => onChangeField('proTip', value)}
        multiline
        rows={2}
      />
      <OverridableField
        label="Next button label"
        stepKey={step.stepKey}
        field="nextLabel"
        defaultValue={step.nextLabel}
        override={override.nextLabel}
        onChange={value => onChangeField('nextLabel', value)}
        help={'Leave blank for the default ("Next" / "Finish").'}
      />
      <OverridableField
        label="Video URL"
        stepKey={step.stepKey}
        field="video"
        defaultValue={step.video}
        override={override.video}
        onChange={value => onChangeField('video', value)}
        help="Paste a YouTube, Loom, or Vimeo share link to embed a video in this step. Leave blank for none."
      />
    </Panel>
  );
};

const OverridableField: React.FC<{
  label: string;
  stepKey: string;
  field: keyof StepOverride;
  defaultValue?: string;
  override?: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  rows?: number;
  help?: string;
}> = ({ label, stepKey, field, defaultValue, override, onChange, multiline, rows = 2, help }) => {
  const effective = override ?? defaultValue ?? '';
  const isOverridden = typeof override === 'string';
  const name = `${field}-${stepKey}`;
  const hint = help
    ? help
    : defaultValue
      ? `Default: "${defaultValue.replace(/\n+/g, ' / ')}"`
      : 'No code default — admin-only field.';

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-700 dark:text-dark-gray-200">{label}</label>
        {isOverridden && (
          <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
            overriding default
          </span>
        )}
      </div>
      {multiline ? (
        <Textarea
          name={name}
          label=""
          value={effective}
          onChange={(v: string) => onChange(v)}
          rows={rows}
        />
      ) : (
        <Input name={name} label="" value={effective} onChange={(v: string) => onChange(v)} />
      )}
      <p className="mt-1 text-[10px] text-gray-400 dark:text-dark-gray-500 truncate">{hint}</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Click-behavior editor (route paths validated inline — appDataUpdate also
// enforces this on the backend, see clickBehaviorSchema in
// libs/.../appDataUpdate.ts).
// ---------------------------------------------------------------------------

const ClickBehaviorEditor: React.FC<{
  cardId: string;
  value?: ClickBehavior;
  onChange: (cb: ClickBehavior) => void;
}> = ({ cardId, value, onChange }) => {
  const kind = value?.kind ?? 'none';

  const setKind = (next: string) => {
    switch (next) {
      case 'tour':
        onChange({ kind: 'tour', tourId: TRIAL_TOUR_OPTIONS[0]?.id ?? '' });
        break;
      case 'video':
        onChange({ kind: 'video', url: '' });
        break;
      case 'route':
        onChange({ kind: 'route', path: '' });
        break;
      case 'none':
      default:
        onChange({ kind: 'none' });
        break;
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Select
        name={`click-kind-${cardId}`}
        label="Behavior"
        value={kind}
        options={CLICK_KIND_OPTIONS}
        onChange={(v: string) => setKind(v)}
        alwaysReturnArray={false}
      />
      {value?.kind === 'tour' && (
        <Select
          name={`click-tour-${cardId}`}
          label="Tour"
          value={value.tourId}
          options={TRIAL_TOUR_OPTIONS.map(t => ({ text: t.label, value: t.id }))}
          onChange={(v: string) => onChange({ kind: 'tour', tourId: v })}
          alwaysReturnArray={false}
        />
      )}
      {value?.kind === 'video' && (
        <>
          <Input
            name={`click-video-url-${cardId}`}
            label="Video URL (YouTube share link, Loom share link, or any embed URL)"
            value={value.url}
            onChange={(v: string) => onChange({ kind: 'video', url: v, title: value.title })}
          />
          <Input
            name={`click-video-title-${cardId}`}
            label="Video title (shown in the modal header, optional)"
            value={value.title || ''}
            onChange={(v: string) => onChange({ kind: 'video', url: value.url, title: v })}
          />
        </>
      )}
      {value?.kind === 'route' && (
        <Input
          name={`click-route-${cardId}`}
          label="Path (e.g., /patients, /schedule, /billing)"
          value={value.path}
          onChange={(v: string) => onChange({ kind: 'route', path: v })}
          errors={
            value.path && !value.path.startsWith('/')
              ? { message: 'Path must start with "/" (e.g., /patients).' }
              : undefined
          }
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared UI primitives
// ---------------------------------------------------------------------------

const Panel: React.FC<{
  title: string;
  subtitle?: string;
  rightSide?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, rightSide, children }) => (
  <div className="bg-white dark:bg-dark-gray-800 border border-gray-200 dark:border-dark-gray-700 rounded-lg p-5">
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-gray-900 dark:text-dark-gray-100 truncate">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-xs font-mono text-gray-400 dark:text-dark-gray-500 truncate">
            {subtitle}
          </p>
        )}
      </div>
      {rightSide}
    </div>
    {children}
  </div>
);

const Divider: React.FC = () => (
  <div className="my-4 border-t border-gray-100 dark:border-dark-gray-700" />
);

const IconButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}> = ({ icon, label, onClick, disabled, tone = 'default' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={[
      'p-1.5 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed',
      tone === 'danger'
        ? 'border-gray-300 dark:border-dark-gray-600 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400'
        : 'border-gray-300 dark:border-dark-gray-600 text-gray-500 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-100',
    ].join(' ')}
  >
    {icon}
  </button>
);

// Tri-state status: in-memory dirty > saved-but-not-deployed > clean.
// Same priority order the home page builder uses so the two editors feel
// consistent to whoever's switching between them.
const DeployStatusPill: React.FC<{
  isDirty: boolean;
  hasUnpublished: boolean;
  saveMessage: string;
}> = ({ isDirty, hasUnpublished, saveMessage }) => {
  if (saveMessage === 'Saved!' || saveMessage === 'Deployed!') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
        <CheckCircleIcon className="h-4 w-4" />
        {saveMessage === 'Deployed!' ? 'Deployed' : 'Saved'}
      </span>
    );
  }

  if (saveMessage === 'Nothing to deploy') {
    return <span className="text-xs font-medium text-gray-500">Nothing to deploy</span>;
  }

  if (saveMessage === 'Error saving' || saveMessage === 'Error deploying') {
    return <span className="text-xs font-medium text-red-600">{saveMessage}</span>;
  }

  if (isDirty) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Unsaved changes
      </span>
    );
  }

  if (hasUnpublished) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-400">
        <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
        Unpublished draft
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
      Up to date
    </span>
  );
};

// ---------------------------------------------------------------------------
// Preview modal
// ---------------------------------------------------------------------------

const PreviewModal: React.FC<{
  open: boolean;
  data: TrialHomeContent;
  selectedCardId: string | null;
  onSelectCard: (id: string) => void;
  onClose: () => void;
}> = ({ open, data, selectedCardId, onSelectCard, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Trial home preview"
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] bg-white dark:bg-dark-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-dark-gray-700 bg-gray-50 dark:bg-dark-gray-800">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-300/80" />
            <span className="ml-3 text-[11px] font-mono uppercase tracking-wider text-gray-500">
              Trial home — preview
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-dark-gray-700 transition-colors"
            aria-label="Close preview"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <TrialHomePreview
            data={data}
            selectedCardId={selectedCardId}
            onSelectCard={onSelectCard}
          />
        </div>
        <div className="px-5 py-2.5 border-t border-gray-100 dark:border-dark-gray-700 bg-gray-50 dark:bg-dark-gray-800 text-[11px] text-gray-500 dark:text-dark-gray-400">
          Click any card to jump to its editor.
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const hasAnyOverride = (override?: StepOverride): boolean =>
  !!override &&
  (typeof override.title === 'string' ||
    typeof override.body === 'string' ||
    typeof override.proTip === 'string' ||
    typeof override.nextLabel === 'string' ||
    typeof override.video === 'string');

// Drop empty step/tour records so the stored blob stays compact and
// `Object.keys` checks remain meaningful at render time.
function pruneEmpty(overrides: OverridesByTour): OverridesByTour {
  const out: OverridesByTour = {};
  for (const [tourId, steps] of Object.entries(overrides)) {
    const cleanedSteps: Record<string, StepOverride> = {};
    for (const [stepKey, stepOverride] of Object.entries(steps)) {
      const nonEmpty = Object.fromEntries(
        Object.entries(stepOverride).filter(([, v]) => typeof v === 'string' && v.trim() !== '')
      );
      if (Object.keys(nonEmpty).length > 0) cleanedSteps[stepKey] = nonEmpty as StepOverride;
    }
    if (Object.keys(cleanedSteps).length > 0) out[tourId] = cleanedSteps;
  }

  return out;
}

export default TrialExperienceEditor;
